import { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, Route, Routes, useLocation, useNavigate, useParams } from "react-router-dom";
import { Show, SignIn, SignUp, SignOutButton, useAuth } from "@clerk/react";
import { toast } from "sonner";
import { useApi, publicGet } from "./api";
import { OrgContext, ROLE_LABEL, can, roleLabel, roleText, useOrg } from "./org";
import { FirstWelcome } from "./FirstWelcome";
import { NoAccess, Shell } from "./Shell";
import Team from "./Team";
import Audit from "./Audit";
import People from "./people/People";
import Person from "./people/Person";
import { Group, Groups } from "./groups/Groups";
import Settings from "./Settings";
import Today from "./care/Today";
import Overview from "./overview/Overview";
import Me from "./Me";
import { LogCareProvider } from "./care/LogCare";
import { BRAND, KnownLockup } from "./Brand";
import Landing from "./marketing/Landing";
import { setChurchTz } from "./care/text";

const ORG_KEY = "care.org";

const Frame = ({ children, testid }) => (
  <div className="min-h-screen bg-paper text-ink font-sans px-5 py-10 sm:py-16" data-testid={testid}>
    <div className="max-w-xl mx-auto">{children}</div>
  </div>
);

const authAppearance = {
  elements: {
    rootBox: "w-full max-w-[420px]",
    cardBox: "!shadow-none !border !border-line !rounded-[20px] w-full",
    card: "!bg-surface !shadow-none",
    headerTitle: "!font-display !text-[24px] !font-semibold !tracking-tight",
    socialButtonsBlockButton: "!h-12 !text-[15px]",
    formButtonPrimary: "!h-12 !text-[15px] !shadow-none",
    formFieldInput: "!h-12",
    footer: "!bg-transparent",
  },
};

const AuthPage = ({ mode }) => (
  <div className="min-h-screen bg-paper flex flex-col lg:flex-row" data-testid={`auth-page-${mode}`}>
    <div className="hidden lg:flex lg:w-[46%] bg-sunk flex-col justify-between px-16 py-14 border-r border-line">
      <span data-testid="auth-wordmark"><KnownLockup size="lg" /></span>
      <div>
        <div className="brand-rule mb-8" />
        <p className="t-greeting max-w-[480px]">{BRAND.tagline}</p>
        <p className="t-lead mt-6 max-w-[420px]">Who needs care, who is caring for them, and what happens next. For pastors and the people who care alongside them.</p>
      </div>
      <p className="t-meta">Private to your church. Confidential notes stay with the people you trust.</p>
    </div>
    <div className="flex-1 flex flex-col items-center justify-center px-5 py-10">
      <div className="lg:hidden w-full max-w-[420px] mb-8">
        <KnownLockup size="md" testid="auth-wordmark-mobile" />
        <p className="t-lead mt-4">{BRAND.tagline}</p>
      </div>
      {mode === "in" ? <SignIn routing="path" path="/sign-in" signUpUrl="/sign-up" withSignUp appearance={authAppearance} /> : <SignUp routing="path" path="/sign-up" signInUrl="/sign-in" appearance={authAppearance} />}
    </div>
  </div>
);

const CreateChurch = ({ onCreated, first }) => {
  const api = useApi();
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const r = await api("/orgs", { method: "POST", body: { name, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone } });
      onCreated(r.organization.id);
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };
  return (
    <Frame testid="create-church-screen">
      <KnownLockup size="sm" />
      <p className="t-label mt-8">Welcome to KNOWN Church Care</p>
      <h1 className="t-greeting mt-3">{first ? "Let's set up your church." : "Set up another church."}</h1>
      <p className="t-lead mt-4">You'll be the Owner. You can invite your pastors and care leaders next.</p>
      <label className="t-label block mt-10" htmlFor="church-name">Church name</label>
      <input id="church-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Grace Fellowship" className="field h-14 px-4 mt-2" data-testid="church-name-input" />
      <button disabled={name.trim().length < 2 || busy} onClick={submit} className="btn btn-primary btn-lg w-full mt-6" data-testid="create-church-btn">Create church</button>
      {first && <SignOutButton><button className="btn btn-quiet btn-sm mt-4" data-testid="create-church-signout-btn">Sign out</button></SignOutButton>}
    </Frame>
  );
};

const Welcome = () => {
  const { me, membership } = useOrg();
  return (
    <div data-testid="home-screen">
      <p className="t-label">Today · {roleLabel(membership, membership.role)}{membership.confidential_access ? " · Confidential Access" : ""}</p>
      <h1 className="t-greeting mt-3" data-testid="home-greeting">Welcome, {me.user.name.split(" ")[0]}.</h1>
      <p className="t-lead mt-4">{roleText(membership, `You're part of ${membership.org_name}. As an Admin you look after the directory, Care Groups, team and settings. Pastoral care stays with pastors and care leaders.`)}</p>
      <a href="/people" className="btn btn-primary btn-sm mt-8 mr-2" data-testid="home-people-link">{membership.role === "leader" ? "My People" : "People"}</a>
      <a href="/prototype" className="btn btn-outline btn-sm mt-8" data-testid="home-prototype-link">Preview the approved design</a>
    </div>
  );
};

const Guard = ({ allow, children }) => (allow ? children : <NoAccess />);

const ChurchApp = ({ me, reload }) => {
  const [orgId, setOrgId] = useState(() => localStorage.getItem(ORG_KEY));
  const membership = me.memberships.find((m) => m.org_id === orgId) || me.memberships[0];
  setChurchTz(membership.org_timezone);
  const api = useApi(membership.org_id);
  const ctx = useMemo(() => ({
    me, membership, api, reload,
    switchOrg: (id) => { localStorage.setItem(ORG_KEY, id); setOrgId(id); },
    onTenantError: (e) => { if (e.message === "Church not found") reload(); },
  }), [me, membership, api, reload]);
  const c = can(membership);
  if (membership.welcome_pending) return <OrgContext.Provider value={ctx}><FirstWelcome /></OrgContext.Provider>;
  return (
    <OrgContext.Provider value={ctx}>
      <Shell>
        <LogCareProvider>
        <Routes>
          <Route path="/" element={c.seesCare ? <Today /> : <Welcome />} />
          <Route path="/team" element={<Guard allow={c.team}><Team /></Guard>} />
          <Route path="/audit" element={<Guard allow={c.audit}><Audit /></Guard>} />
          <Route path="/people" element={<People />} />
          <Route path="/people/:id" element={<Person />} />
          <Route path="/groups" element={<Groups />} />
          <Route path="/groups/:id" element={<Group />} />
          <Route path="/settings" element={<Guard allow={c.directory}><Settings /></Guard>} />
          <Route path="/overview" element={<Guard allow={c.oversee}><Overview /></Guard>} />
          <Route path="/me" element={<Me />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </LogCareProvider>
      </Shell>
    </OrgContext.Provider>
  );
};

const Home = () => {
  const api = useApi();
  const location = useLocation();
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [loadErr, setLoadErr] = useState("");
  const reload = useCallback(() => api("/me").then((r) => { setLoadErr(""); setMe(r); }).catch((e) => { setLoadErr(e.message); toast.error(e.message); }), [api]);
  useEffect(() => { reload(); }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!me && loadErr) return <Frame testid="home-error"><KnownLockup size="sm" /><h1 className="t-greeting mt-8">We couldn't open KNOWN just now.</h1><p className="t-lead mt-4">{loadErr}</p><button onClick={reload} className="btn btn-primary mt-8" data-testid="home-retry-btn">Try again</button></Frame>;
  if (!me) return <Frame testid="home-loading"><p className="t-meta">Loading…</p></Frame>;
  const created = (id) => { localStorage.setItem(ORG_KEY, id); navigate("/"); reload(); };
  if (!me.memberships.length) return <CreateChurch first onCreated={created} />;
  if (location.pathname === "/new-church") return <CreateChurch onCreated={created} />;
  return <ChurchApp me={me} reload={reload} />;
};

const Invite = () => {
  const { token } = useParams();
  const { isSignedIn } = useAuth();
  const api = useApi();
  const navigate = useNavigate();
  const [inv, setInv] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => { setError(""); publicGet(`/invitations/${token}`).then(setInv).catch((e) => setError(e.message)); }, [token, isSignedIn]);
  const accept = async () => {
    try {
      const r = await api(`/invitations/${token}/accept`, { method: "POST" });
      localStorage.setItem(ORG_KEY, r.org_id);
      toast.success(`Welcome to ${inv.org_name}`);
      navigate("/");
    } catch (e) { setError(e.message); }
  };
  const back = encodeURIComponent(window.location.href);
  return (
    <Frame testid="invite-screen">
      <KnownLockup size="sm" testid="invite-brand" />
      {inv && (
        <>
          <h1 className="t-greeting mt-3">{inv.name ? `${inv.name.split(" ")[0]}, you're` : "You're"} invited to {inv.org_name}.</h1>
          <p className="t-lead mt-4 [overflow-wrap:anywhere]">Join as a {inv.role_label || ROLE_LABEL[inv.role]} using {inv.email}.</p>
        </>
      )}
      {error && <p className="t-lead mt-6 text-sun [overflow-wrap:anywhere]" data-testid="invite-error">{error}</p>}
      {error && (
        <div className="flex flex-wrap gap-3 mt-8">
          {isSignedIn && <SignOutButton redirectUrl={window.location.pathname}><button className="btn btn-primary" data-testid="invite-switch-account-btn">Use a different account</button></SignOutButton>}
          <a href="/" className="btn btn-quiet" data-testid="invite-home-btn">Go to KNOWN</a>
        </div>
      )}
      {inv && !error && (
        <>
          {isSignedIn ? (
            <button onClick={accept} className="btn btn-primary btn-lg w-full mt-8" data-testid="invite-accept-btn">Accept invitation</button>
          ) : (
            <>
              <button onClick={() => navigate(`/sign-up?redirect_url=${back}`)} className="btn btn-primary btn-lg w-full mt-8" data-testid="invite-signup-btn">Create your account</button>
              <button onClick={() => navigate(`/sign-in?redirect_url=${back}`)} className="btn btn-quiet w-full mt-3" data-testid="invite-signin-btn">I already have an account</button>
            </>
          )}
        </>
      )}
    </Frame>
  );
};

export default function RealApp() {
  useEffect(() => { document.body.className = "pal-moss"; }, []);
  return (
    <Routes>
      <Route path="/sign-in/*" element={<AuthPage mode="in" />} />
      <Route path="/sign-up/*" element={<AuthPage mode="up" />} />
      <Route path="/invite/:token" element={<Invite />} />
      <Route path="/" element={<><Show when="signed-in"><Home /></Show><Show when="signed-out"><Landing /></Show></>} />
      <Route path="*" element={<><Show when="signed-in"><Home /></Show><Show when="signed-out"><Navigate to="/sign-in" replace /></Show></>} />
    </Routes>
  );
}
